// DER (X9.62) ↔ raw r||s (P1363) for P-256 ECDSA signatures.
//
// The wire format is DER — `SEQUENCE { INTEGER r, INTEGER s }` — because that is what
// Android Keystore and the Secure Enclave produce natively, and the format is defined
// once for every client. WebCrypto, however, speaks raw r||s (two 32-byte big-endian
// integers), so this file is the bridge, in both directions.

/** Raw 64-byte r||s (the WebCrypto output) → DER. */
export function derFromP1363(rs: Uint8Array): Uint8Array {
  if (rs.length !== 64) {
    throw new RangeError(`expected 64 bytes of r||s, got ${rs.length}`);
  }
  const r = derInteger(rs.subarray(0, 32));
  const s = derInteger(rs.subarray(32, 64));
  const out = new Uint8Array(2 + r.length + s.length);
  out[0] = 0x30;
  out[1] = r.length + s.length;
  out.set(r, 2);
  out.set(s, 2 + r.length);
  return out;
}

function derInteger(fixed: Uint8Array): Uint8Array {
  // Minimal-length integer: strip leading zeros...
  let start = 0;
  while (start < fixed.length - 1 && fixed[start] === 0) {
    start++;
  }
  const stripped = fixed.subarray(start);
  // ...but DER integers are signed, so a set top bit needs a zero byte back in front —
  // without it the reader would decode a negative number. The classic bug, both ways.
  const needsPad = (stripped[0]! & 0x80) !== 0;
  const content = needsPad ? new Uint8Array(stripped.length + 1) : stripped;
  if (needsPad) {
    content.set(stripped, 1);
  }
  const out = new Uint8Array(2 + content.length);
  out[0] = 0x02;
  out[1] = content.length;
  out.set(content, 2);
  return out;
}

/** DER → raw 64-byte r||s, or null when the bytes are not a valid signature. */
export function p1363FromDer(der: Uint8Array): Uint8Array | null {
  if (der.length < 8 || der[0] !== 0x30 || der[1] !== der.length - 2) {
    return null;
  }
  const out = new Uint8Array(64);
  let offset = 2;
  for (const half of [0, 32] as const) {
    if (offset + 2 > der.length || der[offset] !== 0x02) {
      return null;
    }
    const length = der[offset + 1]!;
    offset += 2;
    if (length === 0 || offset + length > der.length) {
      return null;
    }
    let content = der.subarray(offset, offset + length);
    offset += length;
    // Tolerate non-minimal leading zeros the way the Dart parser does; a value that
    // still does not fit 32 bytes is simply not a P-256 scalar.
    let lead = 0;
    while (lead < content.length - 1 && content[lead] === 0) {
      lead++;
    }
    content = content.subarray(lead);
    if (content.length > 32) {
      return null;
    }
    out.set(content, half + 32 - content.length);
  }
  return offset === der.length ? out : null;
}
